import { useState, useCallback, useRef } from "react";
import useSettingsStore from "../store/useSettingsStore";
import api from "../lib/api";
import { formatPhone, isValidPhone, getPhoneError } from "../lib/phoneUtils";
import { MessageCircle, X, Send, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const MESSENGERS = ["Telegram", "WhatsApp", "MAX", "Звонок", "Другое"];

export default function FeedbackButton() {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | success
  const [form, setForm] = useState({ name: "", phone: "", messenger: "Telegram", messenger_other: "", message: "" });
  const [loading, setLoading] = useState(false);
  const settings = useSettingsStore((s) => s.settings);
  const content = useSettingsStore((s) => s.content);

  // Swipe-to-close
  const sheetRef = useRef(null);
  const swipeStart = useRef(null);
  const swipeY = useRef(0);

  const onSwipeStart = (e) => { swipeStart.current = e.touches[0].clientY; swipeY.current = 0; };
  const onSwipeMove = (e) => {
    if (!swipeStart.current) return;
    const delta = e.touches[0].clientY - swipeStart.current;
    swipeY.current = delta;
    if (delta > 0 && sheetRef.current) {
      sheetRef.current.style.transform = `translateY(${delta}px)`;
      sheetRef.current.style.transition = "none";
    }
  };
  const onSwipeEnd = () => {
    if (sheetRef.current) {
      sheetRef.current.style.transition = "transform 0.3s ease";
      if (swipeY.current > 100) {
        sheetRef.current.style.transform = "translateY(100%)";
        setTimeout(() => setOpen(false), 300);
      } else {
        sheetRef.current.style.transform = "translateY(0)";
      }
    }
    swipeStart.current = null; swipeY.current = 0;
  };

  const fb = content?.feedback || {};
  const ph = content?.placeholders || {};

  const [phoneError, setPhoneError] = useState("");

  const handlePhoneChange = (value) => {
    const formatted = formatPhone(value);
    setForm({ ...form, phone: formatted });
    setPhoneError(getPhoneError(formatted) || "");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValidPhone(form.phone)) {
      setPhoneError("Введите корректный номер телефона");
      toast.error("Введите корректный номер телефона");
      return;
    }
    setLoading(true);
    try {
      await api.post("/feedback", {
        name: form.name,
        phone: form.phone,
        messenger: form.messenger === "Другое" ? form.messenger_other : form.messenger,
        message: form.message,
      });
      setStatus("success");
      toast.success(fb.success_text || "Заявка отправлена!");
      setTimeout(() => { setOpen(false); setStatus("idle"); setForm({ name: "", phone: "", messenger: "Telegram", messenger_other: "", message: "" }); }, 3000);
    } catch {
      toast.error("Ошибка отправки");
    }
    setLoading(false);
  };

  // Find TG/WA links from contacts_info or settings
  const tgLink = settings?.telegram_link || settings?.contacts_info?.find(c => c.tg_link)?.tg_link || settings?.telegram_channel_url || "";
  const waLink = settings?.whatsapp_link || settings?.contacts_info?.find(c => c.wa_link)?.wa_link || "";

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-24 sm:bottom-6 right-4 sm:right-6 z-[9999] w-14 h-14 rounded-full flex items-center justify-center shadow-2xl transition-all duration-300 hover:scale-110"
        style={{ background: "var(--accent)", boxShadow: "0 4px 20px rgba(212,196,168,0.4)" }}
        data-testid="feedback-float-btn"
        aria-label="Связаться"
      >
        {open ? <X size={24} color="var(--text-inverse)" /> : <MessageCircle size={24} color="var(--text-inverse)" />}
      </button>

      {/* Panel */}
      {open && (
        <>
          {/* Mobile overlay */}
          <div className="md:hidden fixed inset-0 bg-black/50 z-[9998]" onClick={() => setOpen(false)} />

          {/* Desktop overlay */}
          <div className="hidden md:block fixed inset-0 z-[9998]" onClick={() => setOpen(false)} />

          <div
            ref={sheetRef}
            className="fixed z-[9999] md:bottom-38 md:right-6 md:w-[380px] md:rounded-2xl bottom-0 left-0 right-0 md:left-auto rounded-t-3xl md:rounded-t-2xl overflow-hidden shadow-2xl"
            style={{ background: "var(--surface)", border: "1px solid var(--border-accent)", maxHeight: "85vh", transition: "transform 0.3s ease" }}
            data-testid="feedback-panel"
          >
            {/* Handle for mobile swipe */}
            <div
              className="md:hidden flex justify-center pt-3 pb-1 cursor-grab active:cursor-grabbing"
              onTouchStart={onSwipeStart}
              onTouchMove={onSwipeMove}
              onTouchEnd={onSwipeEnd}
            >
              <div className="w-10 h-1 rounded-full bg-[var(--text-secondary)] opacity-50" />
            </div>

            <div className="p-5 overflow-y-auto" style={{ maxHeight: "calc(85vh - 20px)" }}>
              {/* Close button (desktop) */}
              <button
                onClick={() => setOpen(false)}
                className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--accent-muted)] transition-colors"
                data-testid="feedback-close-btn"
              >
                <X size={18} />
              </button>
              {status === "success" ? (
                <div className="text-center py-8" data-testid="feedback-success">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: "var(--accent-muted)" }}>
                    <Send size={28} className="text-[var(--accent)]" />
                  </div>
                  <p className="text-lg font-medium text-[var(--text-primary)]">{fb.success_text || "Мы свяжемся с вами!"}</p>
                </div>
              ) : (
                <>
                  <h3 className="text-lg font-semibold text-[var(--accent)] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {fb.contacts_title || "Написать менеджеру"}
                  </h3>

                  {/* TG/WA Buttons */}
                  <div className="flex gap-3 mb-4">
                    {tgLink && (
                      <a href={tgLink} target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium transition-all bg-[#229ED9]/15 text-[#229ED9] hover:bg-[#229ED9]/25 border border-[#229ED9]/20" data-testid="feedback-tg-btn">
                        <Send size={16} />
                        {content?.contacts?.tg_button || "Telegram"}
                      </a>
                    )}
                    {waLink && (
                      <a href={waLink} target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium transition-all bg-[#25D366]/15 text-[#25D366] hover:bg-[#25D366]/25 border border-[#25D366]/20" data-testid="feedback-wa-btn">
                        <MessageCircle size={16} />
                        {content?.contacts?.wa_button || "WhatsApp"}
                      </a>
                    )}
                  </div>

                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex-1 h-px bg-[var(--border-subtle)]" />
                    <span className="text-xs text-[var(--text-secondary)]">{fb.separator || "или"}</span>
                    <div className="flex-1 h-px bg-[var(--border-subtle)]" />
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-3">
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder={ph.name || "Ваше имя"}
                      required
                      className="w-full input-premium"
                      data-testid="feedback-name-input"
                    />
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => handlePhoneChange(e.target.value)}
                      placeholder={ph.phone || "+7 (___) ___-__-__"}
                      required
                      maxLength={18}
                      className={`w-full input-premium ${phoneError ? "border-red-400" : ""}`}
                      data-testid="feedback-phone-input"
                    />
                    {phoneError && <p className="text-xs text-red-400 -mt-1" data-testid="phone-error">{phoneError}</p>}
                    <div className="relative">
                      <select
                        value={form.messenger}
                        onChange={(e) => setForm({ ...form, messenger: e.target.value })}
                        className="w-full input-premium appearance-none"
                        data-testid="feedback-messenger-select"
                      >
                        {MESSENGERS.map((m) => <option key={m} value={m}>{m}</option>)}
                      </select>
                      <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)] pointer-events-none" />
                    </div>
                    {form.messenger === "Другое" && (
                      <input
                        type="text"
                        value={form.messenger_other}
                        onChange={(e) => setForm({ ...form, messenger_other: e.target.value })}
                        placeholder="Укажите мессенджер"
                        className="w-full input-premium"
                        data-testid="feedback-messenger-other"
                      />
                    )}
                    <textarea
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      placeholder={ph.message || "Ваше сообщение"}
                      rows={3}
                      className="w-full input-premium resize-none"
                      data-testid="feedback-message-input"
                    />
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full btn-premium btn-primary !py-3.5 disabled:opacity-50"
                      data-testid="feedback-submit-btn"
                    >
                      {loading ? "Отправка..." : fb.submit_button || "Отправить заявку"}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}
